import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const TransformerCalculatorPage = () => {
  // State for calculator inputs
  const [loadKW, setLoadKW] = useState(80);
  const [cosPhi, setCosPhi] = useState(0.8);
  const [margin, setMargin] = useState(20);

  // State for calculator results
  const [calculatedKVA, setCalculatedKVA] = useState(0);
  const [recommendedKVA, setRecommendedKVA] = useState(0);

  const stdPowers = [25, 40, 63, 100, 160, 250, 400];

  const roundToStdPower = (kva: number) => {
    let nearest = stdPowers[0];
    for (const p of stdPowers) {
      if (p >= kva) {
        nearest = p;
        break;
      }
      nearest = p;
    }
    return nearest;
  };

  const calculate = () => {
    if (loadKW > 0 && cosPhi > 0 && cosPhi <= 1 && margin >= 0) {
      const s = (loadKW * (1 + margin / 100)) / cosPhi;
      setCalculatedKVA(s);
      setRecommendedKVA(roundToStdPower(s));
    }
  };

  useEffect(() => {
    calculate();
  }, [loadKW, cosPhi, margin]);

  return (
    <>
      <Helmet>
        <title>Калькулятор подбора трансформатора — расчёт мощности онлайн</title>
      </Helmet>
      <main>
        {/* Hero секция */}
        <section className="bg-[#f9fcff] py-16">
          <div className="container mx-auto px-7">
            <h1 className="text-5xl font-semibold text-[#1a3a4f]">
              Калькулятор подбора трансформатора
            </h1>
            <div className="text-lg text-[#2c5a7a] max-w-4xl mt-5 mb-6">
              Бесплатный инженерный инструмент для расчёта мощности трансформатора (кВА) по
              нагрузке, коэффициенту мощности и запасу.
            </div>
            <a
              href="#calculator"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-white bg-[#1e4a6e] hover:bg-[#0f3550]"
            >
              <i className="fas fa-calculator"></i> Перейти к калькулятору
            </a>
            <a
              href="/contacts"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-[#1e4a6e] bg-[#f0f4f8] hover:bg-[#e6edf4] border border-[#cbdde9] ml-4"
            >
              <i className="fas fa-user-tie"></i> Нужна консультация инженера
            </a>
          </div>
        </section>

        {/* Как работает калькулятор */}
        <section className="py-16 border-b border-gray-100">
          <div className="container mx-auto px-7">
            <h2 className="text-3xl font-semibold relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
              Как работает калькулятор?
            </h2>
            <p className="mt-8">
              Вы вводите суммарную активную мощность всех потребителей (кВт), коэффициент мощности
              cos φ (характеризует реактивную нагрузку) и желаемый запас мощности (%). Калькулятор
              вычисляет рекомендуемую полную мощность трансформатора в кВА по формуле:
            </p>
            <p className="bg-[#f0f6fa] p-4 rounded-2xl my-5 text-center text-xl">
              <strong>
                S (кВА) = (P × K<sub>зап</sub>) / cos φ
              </strong>
            </p>
            <p>
              Результат округляется до ближайшего стандартного значения из ряда:{' '}
              <strong className="text-blog-accent">25, 40, 63, 100, 160, 250, 400 кВА</strong> (и
              выше). После расчёта вы увидите подходящие модели трансформаторов и КТПН со ссылками
              на подробные страницы.
            </p>
          </div>
        </section>

        {/* Блок с калькулятором */}
        <section id="calculator" className="py-16">
          <div className="container mx-auto px-7">
            <div className="bg-[#f8fafd] border border-gray-200 rounded-3xl p-10">
              <h2 className="text-3xl font-semibold mb-6">Расчёт мощности трансформатора</h2>
              <div className="grid md:grid-cols-4 gap-6 items-end">
                <div className="calc-field">
                  <label className="font-medium block mb-2">
                    <i className="fas fa-bolt"></i> Суммарная нагрузка (кВт) *
                  </label>
                  <input
                    type="number"
                    value={loadKW}
                    onChange={(e) => setLoadKW(parseFloat(e.target.value))}
                    className="w-full p-3 border border-gray-300 rounded-full"
                  />
                </div>
                <div className="calc-field">
                  <label className="font-medium block mb-2">
                    <i className="fas fa-chart-line"></i> Коэффициент мощности (cos φ)
                  </label>
                  <input
                    type="number"
                    value={cosPhi}
                    onChange={(e) => setCosPhi(parseFloat(e.target.value))}
                    className="w-full p-3 border border-gray-300 rounded-full"
                  />
                </div>
                <div className="calc-field">
                  <label className="font-medium block mb-2">
                    <i className="fas fa-percent"></i> Запас мощности (%)
                  </label>
                  <input
                    type="number"
                    value={margin}
                    onChange={(e) => setMargin(parseFloat(e.target.value))}
                    className="w-full p-3 border border-gray-300 rounded-full"
                  />
                </div>
                <div>
                  <button
                    onClick={calculate}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-medium text-white bg-[#1e4a6e] hover:bg-[#0f3550]"
                  >
                    <i className="fas fa-calculator"></i> Рассчитать
                  </button>
                </div>
              </div>
              {calculatedKVA > 0 && (
                <div className="bg-white border-2 border-blog-accent rounded-2xl p-8 mt-8 text-center">
                  <i className="fas fa-chart-simple text-blog-accent text-3xl"></i>
                  <div className="text-4xl font-bold text-blog-accent my-4">
                    {calculatedKVA.toFixed(1)} кВА → {recommendedKVA} кВА
                  </div>
                  <p>Рекомендованный стандартный трансформатор из ряда мощностей</p>
                  <div className="flex flex-wrap gap-4 justify-center mt-6">
                    {stdPowers.map((p) => (
                      <span
                        key={p}
                        className={`px-5 py-2 rounded-full font-semibold ${p === recommendedKVA ? 'bg-blog-accent text-white' : 'bg-gray-200'}`}
                      >
                        {p} кВА
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default TransformerCalculatorPage;
